import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';
import MDXRenderer from 'gatsby-plugin-mdx/mdx-renderer';

import { Layout, Link } from '$components';
import NextPrevious from '../components/NextPrevious';
import config from '../../config';
import { Edit, StyledHeading, StyledMainWrapper } from '../components/styles/Docs';
import { LocaleProvider } from '../i18n/LocaleContext';
import { locales, defaultLocale } from '../i18n/locales';

const forcedNavOrder = config.sidebar.forcedNavOrder;

export default class MDXRuntimeTest extends Component {
  render() {
    const { data, pageContext } = this.props;
    const locale = (pageContext && pageContext.locale) || defaultLocale;
    const localeConfig = locales[locale] || locales[defaultLocale];
    const localeSlug = localeConfig.slug;
    const isDefault = localeConfig.isDefault;

    if (!data || !data.site || !data.mdx) {
      // This branch renders /docs/404 (and any route with no matching mdx).
      // It returns before the Helmet block below, which left the page with an
      // empty <title> and no robots tag; Search Console had accumulated 2,515
      // impressions on it as an indexed soft 404.
      return (
        <LocaleProvider locale={locale}>
          <Helmet>
            <title>Page not found | LoadFocus Docs</title>
            <meta name="robots" content="noindex,follow" />
          </Helmet>
          <div>Content not found</div>
        </LocaleProvider>
      );
    }
    const {
      allMdx,
      mdx,
      site: {
        siteMetadata: { docsLocation, title },
      },
    } = data;

    const gitHub = require('../components/images/github.svg');

    // Prefix forcedNavOrder entries for non-default locales
    const localeForcedNavOrder = isDefault
      ? forcedNavOrder
      : forcedNavOrder.map(p => `/${localeSlug}${p}`);

    const navItems = allMdx.edges
      .map(({ node }) => node.fields.slug)
      .filter(slug => slug !== '/' && slug !== `/${localeSlug}`)
      .sort()
      .reduce(
        (acc, cur) => {
          if (localeForcedNavOrder.find(url => url === cur)) {
            return { ...acc, [cur]: [cur] };
          }

          // For non-default locale, slug starts with /{localeSlug}/section/...
          // Extract the section prefix
          let prefix;
          if (!isDefault && cur.startsWith(`/${localeSlug}/`)) {
            prefix = `/${localeSlug}/${cur.split('/')[2]}`;
          } else {
            prefix = `/${cur.split('/')[1]}`;
          }

          if (config.gatsby && config.gatsby.trailingSlash) {
            prefix = prefix + '/';
          }

          if (localeForcedNavOrder.find(url => url === prefix)) {
            return { ...acc, [prefix]: [...(acc[prefix] || []), cur] };
          } else {
            return { ...acc, items: [...acc.items, cur] };
          }
        },
        { items: [] }
      );

    const nav = localeForcedNavOrder
      .reduce((acc, cur) => {
        return acc.concat(navItems[cur]);
      }, [])
      .concat(navItems.items)
      .map(slug => {
        if (slug) {
          const found = allMdx.edges.find(({ node }) => node.fields.slug === slug);
          if (found) {
            return { title: found.node.fields.title, url: found.node.fields.slug };
          }
        }
      })
      .filter(Boolean);

    // meta tags
    const metaTitle = mdx.frontmatter.metaTitle;
    const metaDescription = mdx.frontmatter.metaDescription;
    // gatsby-node.js already keeps noindex pages out of the sitemap, but sitemap
    // exclusion is not noindex: Google still indexes them via internal links.
    // Emit the real directive so the frontmatter flag actually does what it says.
    const noindex = mdx.frontmatter.noindex === true;

    let canonicalUrl = config.gatsby.siteUrl;
    canonicalUrl =
      config.gatsby.pathPrefix !== '/' ? canonicalUrl + config.gatsby.pathPrefix : canonicalUrl;
    // The docs home's slug is '/', and nginx 301s /docs/ to /docs: same rule as the hreflang below.
    canonicalUrl = canonicalUrl + (mdx.fields.slug === '/' ? '' : mdx.fields.slug);

    // Strip locale prefix to get content-only path for hreflang
    const stripLocalePrefix = (slug) => {
      if (!isDefault && localeSlug && slug.startsWith(`/${localeSlug}`)) {
        const stripped = slug.substring(localeSlug.length + 1);
        return stripped || '/';
      }
      return slug;
    };

    const contentSlug = stripLocalePrefix(mdx.fields.slug);
    const baseUrl = config.gatsby.siteUrl + (config.gatsby.pathPrefix !== '/' ? config.gatsby.pathPrefix : '');

    return (
      <LocaleProvider locale={locale}>
        <Layout {...this.props} locale={locale}>
          <Helmet htmlAttributes={{ lang: localeConfig.hreflang }}>
            {metaTitle ? <title>{metaTitle}</title> : null}
            {metaTitle ? <meta name="title" content={metaTitle} /> : null}
            {metaDescription ? <meta name="description" content={metaDescription} /> : null}
            {metaTitle ? <meta property="og:title" content={metaTitle} /> : null}
            {metaDescription ? <meta property="og:description" content={metaDescription} /> : null}
            {metaTitle ? <meta property="twitter:title" content={metaTitle} /> : null}
            {metaDescription ? (
              <meta property="twitter:description" content={metaDescription} />
            ) : null}
            {noindex ? <meta name="robots" content="noindex,follow" /> : null}
            <link rel="canonical" href={canonicalUrl} />
            {Object.entries(locales).map(([code, conf]) => {
              const altPrefix = conf.isDefault ? '' : `/${conf.slug}`;
              const altUrl = `${baseUrl}${altPrefix}${contentSlug === '/' ? '' : contentSlug}`;
              return <link key={code} rel="alternate" hreflang={conf.hreflang} href={altUrl} />;
            })}
            <link rel="alternate" hreflang="x-default" href={`${baseUrl}${contentSlug === '/' ? '' : contentSlug}`} />
          </Helmet>
          <div className={'titleWrapper'}>
            <StyledHeading>{mdx.fields.title}</StyledHeading>
            <Edit className={'mobileView'}>
              {docsLocation && (
                <Link className={'gitBtn'} to={`${docsLocation}/${mdx.parent.relativePath}`}>
                  <img src={gitHub} alt={'Github logo'} /> Edit on GitHub
                </Link>
              )}
            </Edit>
          </div>
          <StyledMainWrapper>
            <MDXRenderer>{mdx.body}</MDXRenderer>
          </StyledMainWrapper>
          <div className={'addPaddTopBottom'}>
            <NextPrevious mdx={mdx} nav={nav} />
          </div>
        </Layout>
      </LocaleProvider>
    );
  }
}

export const pageQuery = graphql`
  query($id: String!, $locale: String!) {
    site {
      siteMetadata {
        title
        docsLocation
      }
    }
    mdx(fields: { id: { eq: $id } }) {
      fields {
        id
        title
        slug
        locale
      }
      body
      tableOfContents
      parent {
        ... on File {
          relativePath
        }
      }
      frontmatter {
        metaTitle
        metaDescription
        order
        noindex
      }
    }
    allMdx(filter: { fields: { locale: { eq: $locale } } }) {
      edges {
        node {
          fields {
            slug
            title
          }
        }
      }
    }
  }
`;
